import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";
import { usePayrollPeriods } from "../../../../hooks/usePayrollPeriods";
import { Select } from "../../../../../common/Select";

const LeaveEncashmentSyncSummary = ({ isOpen, onClose, onConfirm, selectedData, selectedYear, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [targetProcess, setTargetProcess] = useState("payroll"); // ADDED: Toggle state

  const { periods, loadingPeriods } = usePayrollPeriods(selectedYear);

  if (!isOpen) return null;

  const headers = ["ID", "Full Name", "Encashable Days", "Total Value"];
  const accessors = ["employeeId", "fullName", "totalEncashable", "totalGross"];

  const customRender = (row, accessor) => {
    if (accessor === "totalEncashable") return <span className="font-bold text-blue-700">{row[accessor]} Days</span>;
    if (accessor === "totalGross") return <span className="font-extrabold text-blue-900">₱{row[accessor].toLocaleString()}</span>;
    return null;
  };

  const grandTotalValue = selectedData.reduce((sum, item) => sum + parseFloat(item.totalGross || 0), 0);
  const totalEmployees = selectedData.length;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto" style={{ marginTop: 0 }}>
      <div className="bg-white w-full md:w-[90%] lg:w-[70%] rounded-lg shadow-2xl h-auto max-h-[90vh] flex flex-col relative">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-blue-900 tracking-tight">Sync to Payroll Summary</h2>
            <p className="text-xs text-gray-500 uppercase font-semibold">Review selected employees before posting</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Content Table */}
        <div className="p-6 overflow-y-auto flex-grow">

          {/* STEP 1: SELECT DESTINATION */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col gap-2">
            <span className="text-sm font-bold text-blue-800 uppercase">Step 1: Select Destination Staging</span>
            <div className="flex gap-6 mt-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
                <input 
                  type="radio" 
                  className="w-4 h-4"
                  name="targetProcess"
                  value="payroll" 
                  checked={targetProcess === "payroll"} 
                  onChange={(e) => setTargetProcess(e.target.value)} 
                />
                Regular Payroll
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
                <input 
                  type="radio" 
                  className="w-4 h-4"
                  name="targetProcess"
                  value="finalpay" 
                  checked={targetProcess === "finalpay"} 
                  onChange={(e) => setTargetProcess(e.target.value)} 
                />
                Final Pay
              </label>
            </div>
          </div>

          {/* STEP 2: SELECT PERIOD */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-2">
            <span className="text-sm font-bold text-amber-800 uppercase">Step 2: Select Target Payroll Period</span>
            <div className="w-full md:w-1/2">
              <Select
                placeholder={loadingPeriods ? "Loading periods..." : "Select Payroll Period..."}
                options={periods}
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                isDisabled={loadingPeriods}
              />
            </div>
            <p className="text-[11px] text-amber-700 italic m-0">
              * Encashments will be posted to the {targetProcess === 'payroll' ? 'Other Additions' : 'Final Pay Additions'} of this period.
            </p>
          </div>

          <CreateTable
            tableName="SyncSummaryTable"
            headers={headers}
            accessors={accessors}
            data={selectedData}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={5}
            totalItems={selectedData.length}
            customCellRenderer={customRender}
          />

          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg flex justify-between items-center">
            <div className="text-sm">
              <span className="text-gray-600">Total Employees: </span>
              <span className="font-bold text-blue-900">{totalEmployees}</span>
            </div>
            <div className="text-lg">
              <span className="text-gray-600 font-medium">Total Amount: </span>
              <span className="font-black text-blue-900">₱{grandTotalValue.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button disabled={loading} onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg cursor-pointer border-none transition-colors font-bold">
            Cancel
          </button>
          <button
            disabled={loading || selectedData.length === 0 || !selectedPeriodId}
            onClick={() => onConfirm(selectedPeriodId, targetProcess)} // MODIFIED: Pass targetProcess
            className="bg-green-600 hover:bg-green-700 text-white px-10 py-2 rounded-lg cursor-pointer border-none transition-colors font-bold shadow-md disabled:opacity-50"
          >
            {loading ? "Processing..." : `Confirm & Post to ${targetProcess === "payroll" ? "Payroll" : "Final Pay"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveEncashmentSyncSummary;